/**
 * Card-grid and player helpers for the tutorials catalog.
 */

import {
  TUTORIAL_MODE_OPTIONS,
  TUTORIAL_VIDEO_FALLBACK,
} from '@/components/tutorials/tutorial-configs';
import { getTutorialVideoUrlCandidates } from '@/config/storage';
import type { AppMode } from '@/contexts/ModeContext';
import type { TutorialVideo, TutorialVideoMode } from '@/types/tutorial.types';

/** Catalog fetch mode: présentiel stays; null / remote use the remote catalog. */
export function tutorialCatalogMode(mode: AppMode | null): AppMode {
  return mode === 'in_person' ? 'in_person' : 'remote';
}

/** Loaded rows, or the legacy fallback only when the catalog request failed. */
export function tutorialCatalogRows(
  data: TutorialVideo[] | undefined,
  isError: boolean,
): TutorialVideo[] {
  if (data) return data;
  return isError ? TUTORIAL_VIDEO_FALLBACK : [];
}

/** Keep videos for the active screen: that mode and hybrid. */
export function tutorialRowsVisibleInMode(videos: TutorialVideo[], mode: AppMode): TutorialVideo[] {
  return videos.filter((video) => video.mode === mode || video.mode === 'hybrid');
}

/** Row actions only after a successful catalog fetch, and only with `action-edit-row`. */
export function canEditTutorialRows(canEdit: boolean, hasCatalogData: boolean): boolean {
  return canEdit && hasCatalogData;
}

/** True when no role is selected — administration-only catalog row. */
export function isTutorialAdminOnly(video: TutorialVideo): boolean {
  return video.roleIds.length === 0;
}

/** Filename stored under `Tutorials/`, or null when the URL is a company upload. */
function legacyTutorialFilename(videoUrl: string): string | null {
  const marker = '/Tutorials/';
  const index = videoUrl.indexOf(marker);
  if (index < 0) return null;
  const encoded = videoUrl.slice(index + marker.length).replace(/\.mp4$/i, '');
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

/** French label for a tutorial mode. */
export function tutorialModeLabel(mode: TutorialVideoMode): string {
  return TUTORIAL_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? mode;
}

/** `tel:` href for a displayed phone. French leading 0 becomes +33. */
export function tutorialPhoneHref(phone: string): string {
  const compact = phone.replace(/[^\d+]/g, '');
  if (compact.startsWith('+')) return `tel:${compact}`;
  if (compact.startsWith('0')) return `tel:+33${compact.slice(1)}`;
  return `tel:${compact}`;
}

/** Archive modal noun: `cette vidéo` or `la vidéo « name »`. */
export function formatTutorialArchiveModalItemName(
  video: TutorialVideo | null | undefined,
): string {
  return video?.name ? `la vidéo « ${video.name} »` : 'cette vidéo';
}

/** Playback URLs. Legacy files try every accent spelling; uploads use their stored URL. */
export function tutorialPlaybackUrls(video: TutorialVideo): string[] {
  const filename = legacyTutorialFilename(video.videoUrl);
  if (!filename) return [video.videoUrl];
  return getTutorialVideoUrlCandidates(filename);
}
